import { useState, useEffect } from 'react';
import { X, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

// ACT!-style "Edit list…" dialog for the ID/Status droplist.
// Renaming keeps the underlying value so existing contacts still match.
export default function StatusListEditor({ open, onClose, statuses, onSave }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setRows((statuses || []).map((s) => ({ ...s })));
  }, [open, statuses]);

  if (!open) return null;

  const setLabel = (i, label) => setRows((r) => r.map((row, j) => (j === i ? { ...row, label } : row)));
  const removeRow = (i) => setRows((r) => r.filter((_, j) => j !== i));
  const addRow = () => {
    if (rows.length >= 20) { toast.error('Max 20 statuses'); return; }
    setRows((r) => [...r, { value: '', label: '' }]);
  };

  const save = async () => {
    const clean = rows
      .map((r) => ({ value: r.value, label: r.label.trim() }))
      .filter((r) => r.label);
    if (!clean.length) { toast.error('Keep at least one status'); return; }
    setSaving(true);
    try {
      await onSave(clean);
      toast.success('ID/Status list saved');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the list');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="status-editor-overlay">
      <div className="bg-white border border-[#B0B0B0] shadow-2xl w-full max-w-[380px]" onClick={(e) => e.stopPropagation()}>
        <div className="px-3 py-2 bg-[#0B5394] text-white text-[12px] font-bold flex items-center justify-between">
          Edit ID/Status List
          <button onClick={onClose} className="p-0.5 hover:bg-white/20 rounded" data-testid="status-editor-close" aria-label="Close">
            <X size={13} weight="bold" />
          </button>
        </div>
        <div className="p-3 space-y-1.5 max-h-[320px] overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input
                value={r.label}
                onChange={(e) => setLabel(i, e.target.value)}
                maxLength={30}
                placeholder="Status name…"
                className="flex-1 h-[26px] px-2 text-[12px] border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394]"
                data-testid={`status-editor-input-${i}`}
              />
              <button onClick={() => removeRow(i)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Remove" data-testid={`status-editor-remove-${i}`}>
                <X size={13} weight="bold" />
              </button>
            </div>
          ))}
          <button onClick={addRow} className="flex items-center gap-1 text-[11px] text-[#0B5394] font-bold hover:underline mt-1" data-testid="status-editor-add">
            <Plus size={12} weight="bold" /> Add status
          </button>
          <p className="text-[10px] text-[#888] pt-1">
            Renaming keeps existing contacts matched. Deleting a status doesn&apos;t change contacts that already use it.
          </p>
        </div>
        <div className="px-3 py-2 border-t border-[#DDD] flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1 text-[11px] border border-[#999] bg-white hover:bg-[#F5F5F5]" data-testid="status-editor-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="px-3 py-1 text-[11px] font-bold bg-[#0B5394] hover:bg-[#094180] text-white disabled:opacity-60" data-testid="status-editor-save">
            {saving ? 'Saving…' : 'Save List'}
          </button>
        </div>
      </div>
    </div>
  );
}
